// Der Rahmen: Seitenleiste (Programmwechsel, Seiten des Programms, einklappbar), Befehlspalette, und im Inhaltsbereich
// die Website des Programms, zu dem die Adresse gehört (/app/… über den Torweg). Wechselt man innerhalb eines
// Programms, navigiert die Website selbst; wechselt das Programm, lädt der Rahmen die andere Website. Das Programm
// meldet Ort und Titel zurück – die Adresszeile zeigt immer die echte Seite (/posteingang/verschieben).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Laden } from "@imho/ui/bausteine/einfach";
import { Befehlspalette } from "@imho/ui/huelle/Befehlspalette";
import { Seitenleiste } from "@imho/ui/huelle/Seitenleiste";
import { useEingeklappt } from "@imho/ui/huelle/seitenleisteLogik";
import { PROGRAMMNAME } from "@imho/ui/kern/marke";
import { istNachricht, PROGRAMM_BASIS, type Nachricht } from "@imho/ui/kern/rahmen";
import { useSitzung } from "@imho/ui/kern/sitzung";
import { OHNE_RAHMEN, programmFuerPfad } from "@imho/ui/navigation/katalog";

const nurPfad = (p: string) => p.split("?")[0];

export function Rahmen() {
  const { ich, laedt, abmelden } = useSitzung();
  const ort = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pfad = `${ort.pathname}${ort.search}`;
  const programm = programmFuerPfad(ort.pathname);
  const [eingeklappt, setEingeklappt] = useEingeklappt();
  const [menue, setMenue] = useState(false);
  const [palette, setPalette] = useState(false);
  const [quelle] = useState(() => `${PROGRAMM_BASIS}${pfad}`);
  const fenster = useRef<HTMLIFrameElement>(null);
  const imProgramm = useRef(pfad);  // wo die Website im Inhaltsbereich gerade ist
  const oben = useRef(pfad);
  oben.current = pfad;

  useEffect(() => setMenue(false), [pfad]);
  // Vollbild-Seiten (ZeitApp) laufen ohne Rahmen: ganz neu laden, der Torweg liefert dann direkt das Programm
  const ohneRahmen = Object.keys(OHNE_RAHMEN).some((p) => ort.pathname === p || ort.pathname.startsWith(`${p}/`));
  useEffect(() => { if (ohneRahmen) window.location.replace(pfad); }, [ohneRahmen, pfad]);

  // Adresse oben hat sich geändert (Seitenleiste, Palette, Zurück): Website navigieren oder anderes Programm laden
  useEffect(() => {
    if (pfad === imProgramm.current) return;
    const bisher = programmFuerPfad(nurPfad(imProgramm.current));
    imProgramm.current = pfad;
    const w = fenster.current?.contentWindow;
    if (!w) return;
    if (bisher === programm) w.postMessage({ typ: "imho:gehe", pfad } satisfies Nachricht, window.location.origin);
    else w.location.replace(`${PROGRAMM_BASIS}${pfad}`);  // ohne zusätzlichen Eintrag im Verlauf
  }, [pfad, programm]);

  // Nachrichten der Website im Inhaltsbereich
  useEffect(() => {
    const nachricht = (e: MessageEvent) => {
      if (!istNachricht(e) || e.source !== fenster.current?.contentWindow) return;
      const n = e.data;
      if (n.typ === "imho:ort") {
        // Adresse eines anderen Programms (navigate() dorthin, z. B. Tools › Bilder komprimieren → Posteingang › Fotos):
        // nicht übernehmen – das Programm zeigt „Fremd“ und bittet gleich mit „gehe“ darum, das andere Programm zu laden.
        // Würde der Rahmen sie hier übernehmen, hielte er sie für schon geladen und das Programm bliebe beim Laden stehen.
        if (programmFuerPfad(nurPfad(n.pfad)) !== programmFuerPfad(nurPfad(imProgramm.current))) return;
        imProgramm.current = n.pfad;
        document.title = `${n.titel} · ${PROGRAMMNAME}`;
        if (n.pfad !== oben.current) navigate(n.pfad, { replace: true });
      } else if (n.typ === "imho:gehe" && nurPfad(n.pfad) === "/anmelden") {
        // Die Website im Inhalt hat das Sitzungsende bemerkt (Abmelden dort, Passwort geändert, Sitzung abgelaufen): erst
        // „ich“ neu fragen – sonst schickt /anmelden mit dem alten Benutzer des Rahmens gleich wieder zurück
        void qc.refetchQueries({ queryKey: ["ich"] }).then(() => navigate(n.pfad, { replace: true }));
      } else if (n.typ === "imho:gehe") navigate(n.pfad);
      else if (n.typ === "imho:palette") setPalette(true);
      else if (n.typ === "imho:menue") setMenue(true);
    };
    window.addEventListener("message", nachricht);
    return () => window.removeEventListener("message", nachricht);
  }, [navigate, qc]);

  if (laedt || ohneRahmen) return <div className="im-root im-vollbild"><Laden /></div>;
  if (!ich) {
    const zurueck = ort.pathname === "/" ? "" : `?zurueck=${encodeURIComponent(pfad)}`;
    return <Navigate to={`/anmelden${zurueck}`} replace />;
  }
  return (
    <div className="im-root im-shell im-rahmen" data-menue={menue ? "offen" : undefined} data-eingeklappt={eingeklappt ? "ja" : undefined}>
      <Seitenleiste programm={programm} pfad={ort.pathname} gehe={(p) => navigate(p)} mitProgrammwahl
        eingeklappt={eingeklappt} setEingeklappt={setEingeklappt}
        abmelden={() => { void abmelden().then(() => navigate("/anmelden")); }} />
      {menue && <div className="im-nav-schleier" aria-hidden="true" onClick={() => setMenue(false)} />}
      <div className="im-main im-rahmen__inhalt">
        <iframe ref={fenster} name="programm" title="Programm" src={quelle} />
      </div>
      <Befehlspalette offen={palette} setOffen={setPalette} gehe={(p) => navigate(p)} />
    </div>
  );
}
